import { ChangeDetectionStrategy, Component, ViewEncapsulation, input, output } from '@angular/core';

export type CardVariant = 'elevated' | 'interactive';

/**
 * Card Component
 *
 * A generic card container with header, body, and footer slots.
 * Supports elevated (static) and interactive (clickable) variants.
 *
 * @example
 * ```html
 * <zag-card>
 *   <div card-header class="zag-card-header-content">
 *     <zag-avatar class="zag-card-avatar" [name]="person.name" size="md" />
 *     <div class="zag-card-header-text">
 *       <h3 class="zag-card-title">{{ person.name }}</h3>
 *       <p class="zag-card-subtitle">{{ person.role }}</p>
 *     </div>
 *   </div>
 *
 *   <p>{{ person.bio }}</p>
 *
 *   <div card-footer>
 *     <button zagButton zagType="secondary">Edit</button>
 *   </div>
 * </zag-card>
 * ```
 *
 * @example Interactive card
 * ```html
 * <zag-card variant="interactive" (cardClick)="openDetails(team)">
 *   <div card-header>
 *     <h3 class="zag-card-title">{{ team.name }}</h3>
 *   </div>
 *   <p>{{ team.description }}</p>
 * </zag-card>
 * ```
 */
@Component({
  selector: 'zag-card',
  templateUrl: './card.html',
  styleUrls: ['./card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.zag-card]': 'true',
    '[class.zag-card--elevated]': 'variant() === "elevated"',
    '[class.zag-card--interactive]': 'variant() === "interactive"',
    '[attr.tabindex]': 'variant() === "interactive" ? 0 : null',
    '[attr.role]': 'variant() === "interactive" ? "button" : "article"',
    '(click)': 'onCardClick($event)',
    '(keydown.enter)': 'onCardKeydown($event)',
    '(keydown.space)': 'onCardKeydown($event)',
    '(keyup.space)': 'onCardKeyup($event)',
  },
})
export class Card {
  /** Card variant: elevated (static) or interactive (clickable) */
  readonly variant = input<CardVariant>('elevated');

  /** Emitted when interactive card is clicked or activated via keyboard */
  readonly cardClick = output<void>();

  onCardClick(event: MouseEvent): void {
    if (!this.isInteractive() || event.defaultPrevented) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;
    if (target && currentTarget && target !== currentTarget) {
      const interactiveElement = target.closest('button, a, input, textarea, select, [role="button"], [role="link"]');
      if (interactiveElement && interactiveElement !== currentTarget && currentTarget.contains(interactiveElement)) {
        return;
      }
    }

    this.cardClick.emit();
  }

  onCardKeydown(event: Event): void {
    if (!this.isInteractive()) {
      return;
    }

    if ((event as KeyboardEvent).key === ' ') {
      event.preventDefault();
    }

    if ((event as KeyboardEvent).key === 'Enter') {
      this.cardClick.emit();
    }
  }

  onCardKeyup(event: Event): void {
    if (this.isInteractive() && (event as KeyboardEvent).key === ' ') {
      this.cardClick.emit();
    }
  }

  private isInteractive(): boolean {
    return this.variant() === 'interactive';
  }
}
